import React, { useState, useEffect } from 'react';
import { FiSearch, FiChevronDown, FiChevronUp, FiHelpCircle, FiMessageSquare } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import { API_URL } from '../../api/client';

interface FAQ {
  faq_id: string;
  category: string;
  question: string;
  answer: string;
  display_order: number;
}

interface CategoryCount {
  category: string;
  faq_count: number;
}

const Help: React.FC = () => {
  const [faqs, setFaqs] = useState<FAQ[]>([]);
  const [categories, setCategories] = useState<CategoryCount[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [expandedFaqs, setExpandedFaqs] = useState<Set<string>>(new Set());

  useEffect(() => {
    fetchCategories();
    fetchFAQs();
  }, [selectedCategory, searchQuery]);

  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/customer/faqs/categories`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const result = await response.json();
      if (result.success) {
        setCategories(result.data);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchFAQs = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (selectedCategory) params.append('category', selectedCategory);
      if (searchQuery) params.append('search', searchQuery);

      const response = await fetch(
        `${API_URL}/customer/faqs?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const result = await response.json();
      if (result.success) {
        setFaqs(result.data);
        // Auto-expand all if searching
        if (searchQuery) {
          setExpandedFaqs(new Set(result.data.map((faq: FAQ) => faq.faq_id)));
        } else {
          setExpandedFaqs(new Set());
        }
      }
    } catch (error) {
      console.error('Error fetching FAQs:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleFaq = (faqId: string) => {
    const newExpanded = new Set(expandedFaqs);
    if (newExpanded.has(faqId)) {
      newExpanded.delete(faqId);
    } else {
      newExpanded.add(faqId);
    }
    setExpandedFaqs(newExpanded);
  };

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    fetchFAQs();
  };

  // Group FAQs by category
  const groupedFaqs = faqs.reduce((acc, faq) => {
    if (!acc[faq.category]) {
      acc[faq.category] = [];
    }
    acc[faq.category].push(faq);
    return acc;
  }, {} as Record<string, FAQ[]>);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-indigo-50/20 to-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* Elegant Header */}
        <div className="text-center mb-12">
          <div className="relative w-20 h-20 mx-auto mb-8">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-3xl transform rotate-6"></div>
            <div className="relative w-full h-full bg-gradient-to-br from-blue-600 via-blue-500 to-indigo-600 rounded-3xl flex items-center justify-center shadow-2xl shadow-blue-500/50">
              <FiHelpCircle size={40} className="text-white" strokeWidth={2} />
            </div>
          </div>
          <h1 className="text-5xl font-extrabold text-gray-900 mb-4 tracking-tight">How can we help you?</h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
            Search our knowledge base for answers to common questions
          </p>
        </div>

        {/* Enhanced Search Bar */}
        <div className="mb-12">
          <form onSubmit={handleSearch}>
            <div className="relative max-w-3xl mx-auto">
              <div className="absolute left-6 top-1/2 transform -translate-y-1/2 text-gray-400">
                <FiSearch size={24} strokeWidth={2.5} />
              </div>
              <input
                type="text"
                placeholder="What are you looking for?"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-16 pr-8 py-6 text-lg border-2 border-gray-200 rounded-3xl focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 shadow-lg hover:shadow-xl transition-all font-medium bg-white"
              />
            </div>
          </form>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-10">
          {/* Enhanced Sidebar - Categories */}
          <div className="lg:col-span-1">
            <div className="sticky top-6 space-y-6">
              <div className="bg-white rounded-3xl shadow-lg border border-gray-100 p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-5 uppercase tracking-wide text-sm">Browse by Category</h3>
                <div className="space-y-2">
                  <button
                    onClick={() => setSelectedCategory('')}
                    className={`w-full text-left px-5 py-4 rounded-2xl transition-all duration-300 font-semibold text-sm ${
                      !selectedCategory
                        ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg shadow-blue-500/40 scale-105'
                        : 'hover:bg-gray-50 text-gray-700 border border-gray-200'
                    }`}
                  >
                    <div className="flex justify-between items-center">
                      <span>All Categories</span>
                    </div>
                  </button>
                  {categories.map((cat) => (
                    <button
                      key={cat.category}
                      onClick={() => setSelectedCategory(cat.category)}
                      className={`w-full text-left px-5 py-4 rounded-2xl transition-all duration-300 font-semibold text-sm ${
                        selectedCategory === cat.category
                          ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg shadow-blue-500/40 scale-105'
                          : 'hover:bg-gray-50 text-gray-700 border border-gray-200'
                      }`}
                    >
                      <div className="flex justify-between items-center">
                        <span>{cat.category}</span>
                        <span className={`text-xs px-3 py-1 rounded-xl font-bold ${
                          selectedCategory === cat.category
                            ? 'bg-white/20 text-white'
                            : 'bg-gray-100 text-gray-600'
                        }`}>
                          {cat.faq_count}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Enhanced Contact Support Card */}
              <div className="relative overflow-hidden bg-gradient-to-br from-blue-600 via-blue-500 to-indigo-600 rounded-3xl p-7 border border-blue-400 shadow-2xl shadow-blue-500/50">
                <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16"></div>
                <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full -ml-12 -mb-12"></div>
                <div className="relative">
                  <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mb-5">
                    <FiMessageSquare className="text-white" size={28} strokeWidth={2.5} />
                  </div>
                  <h3 className="text-xl font-extrabold text-white mb-3">Still Need Help?</h3>
                  <p className="text-blue-100 text-sm mb-6 leading-relaxed">
                    Our support team is ready to assist you with any questions.
                  </p>
                  <Link
                    to="/customer/support"
                    className="block w-full bg-white text-blue-600 text-center px-5 py-4 rounded-2xl hover:bg-blue-50 font-bold transition-all shadow-xl hover:shadow-2xl hover:-translate-y-0.5 text-base"
                  >
                    Contact Support
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {/* Enhanced Main Content - FAQs */}
          <div className="lg:col-span-3">
            {Object.keys(groupedFaqs).length === 0 ? (
              <div className="bg-white rounded-3xl shadow-md border border-gray-100 p-20 text-center">
                <div className="max-w-md mx-auto">
                  <div className="relative w-24 h-24 mx-auto mb-8">
                    <div className="absolute inset-0 bg-gradient-to-br from-gray-100 to-gray-200 rounded-3xl transform rotate-6"></div>
                    <div className="relative w-full h-full bg-gradient-to-br from-gray-400 to-gray-500 rounded-3xl flex items-center justify-center shadow-lg">
                      <FiHelpCircle className="text-white" size={48} strokeWidth={1.5} />
                    </div>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">No answers found</h3>
                  <p className="text-base text-gray-600 mb-8 leading-relaxed">
                    {searchQuery
                      ? "We couldn't find any FAQs matching your search. Try different keywords or browse by category."
                      : 'No FAQs are available at the moment. Please check back later or contact support.'}
                  </p>
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery('')}
                      className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-bold rounded-2xl hover:from-blue-700 hover:to-indigo-700 transition-all shadow-xl shadow-blue-500/40 hover:shadow-2xl hover:shadow-blue-500/50 hover:-translate-y-0.5"
                    >
                      Clear search
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <div className="space-y-7">
                {Object.entries(groupedFaqs).map(([category, categoryFaqs]) => (
                  <div key={category} className="bg-white rounded-3xl shadow-lg border border-gray-100 overflow-hidden">
                    <div className="bg-gradient-to-r from-blue-600 via-blue-500 to-indigo-600 px-8 py-5 border-b border-blue-500">
                      <h2 className="text-2xl font-extrabold text-white tracking-tight">{category}</h2>
                    </div>
                    <div className="divide-y divide-gray-100">
                      {categoryFaqs
                        .sort((a, b) => a.display_order - b.display_order)
                        .map((faq) => (
                          <div key={faq.faq_id} className="group transition-all">
                            <button
                              onClick={() => toggleFaq(faq.faq_id)}
                              className={`w-full px-8 py-6 flex items-start justify-between gap-6 text-left transition-all duration-300 ${
                                expandedFaqs.has(faq.faq_id)
                                  ? 'bg-blue-50'
                                  : 'hover:bg-gray-50'
                              }`}
                            >
                              <h3 className={`flex-1 text-lg font-bold transition-colors leading-relaxed ${
                                expandedFaqs.has(faq.faq_id)
                                  ? 'text-blue-600'
                                  : 'text-gray-900 group-hover:text-blue-600'
                              }`}>
                                {faq.question}
                              </h3>
                              <div className="flex-shrink-0 mt-1.5">
                                <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-300 ${
                                  expandedFaqs.has(faq.faq_id)
                                    ? 'bg-blue-600 shadow-lg shadow-blue-500/40'
                                    : 'bg-gray-100 group-hover:bg-blue-100'
                                }`}>
                                  {expandedFaqs.has(faq.faq_id) ? (
                                    <FiChevronUp className="text-white" size={22} strokeWidth={2.5} />
                                  ) : (
                                    <FiChevronDown className="text-gray-600 group-hover:text-blue-600" size={22} strokeWidth={2.5} />
                                  )}
                                </div>
                              </div>
                            </button>
                            {expandedFaqs.has(faq.faq_id) && (
                              <div className="px-8 pb-8 animate-fadeIn bg-blue-50">
                                <div className="bg-white text-gray-700 leading-relaxed whitespace-pre-wrap p-6 rounded-2xl border-2 border-blue-100 shadow-inner">
                                  {faq.answer}
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(-8px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fadeIn {
          animation: fadeIn 0.2s ease-out;
        }
      `}</style>

      <style>{`
        /* Mobile responsive enhancements */
        @media (max-width: 1024px) {
          .grid.grid-cols-1.lg\\:grid-cols-4 {
            grid-template-columns: 1fr !important;
          }

          .lg\\:col-span-1,
          .lg\\:col-span-3 {
            grid-column: span 1 !important;
          }

          .sticky {
            position: relative !important;
          }
        }

        @media (max-width: 768px) {
          .text-5xl {
            font-size: 2rem !important;
          }

          .text-4xl {
            font-size: 1.75rem !important;
          }

          .text-3xl {
            font-size: 1.5rem !important;
          }

          .text-2xl {
            font-size: 1.25rem !important;
          }

          .text-xl {
            font-size: 1.0625rem !important;
          }

          .text-lg {
            font-size: 1rem !important;
          }

          .px-8 {
            padding-left: 1.25rem !important;
            padding-right: 1.25rem !important;
          }

          .py-10 {
            padding-top: 2rem !important;
            padding-bottom: 2rem !important;
          }

          .p-20 {
            padding: 2rem !important;
          }

          .p-7 {
            padding: 1.25rem !important;
          }

          .p-6 {
            padding: 1rem !important;
          }

          .gap-10 {
            gap: 1.5rem !important;
          }

          .gap-7 {
            gap: 1rem !important;
          }

          .max-w-3xl,
          .max-w-2xl,
          .max-w-md {
            max-width: 100% !important;
          }

          .w-20.h-20 {
            width: 4rem !important;
            height: 4rem !important;
          }

          .w-14.h-14 {
            width: 3rem !important;
            height: 3rem !important;
          }

          button {
            min-height: 44px;
            touch-action: manipulation;
          }
        }

        @media (max-width: 480px) {
          .text-5xl {
            font-size: 1.75rem !important;
          }

          .text-2xl {
            font-size: 1.125rem !important;
          }

          .text-xl {
            font-size: 1rem !important;
          }

          .px-8,
          .px-7 {
            padding-left: 1rem !important;
            padding-right: 1rem !important;
          }

          .py-10 {
            padding-top: 1.5rem !important;
            padding-bottom: 1.5rem !important;
          }

          .p-20 {
            padding: 1.5rem !important;
          }

          .p-7,
          .p-6 {
            padding: 1rem !important;
          }

          .w-20.h-20 {
            width: 3.5rem !important;
            height: 3.5rem !important;
          }

          .w-14.h-14 {
            width: 2.75rem !important;
            height: 2.75rem !important;
          }

          .w-10.h-10 {
            width: 2.25rem !important;
            height: 2.25rem !important;
          }
        }
      `}</style>
    </div>
  );
};

export default Help;
